// PropertyLightbox — shared image viewer used by both detail surfaces.
//
// Two visual variants live in here:
//   - `fullScreen={true}` (mobile hero): black full-screen dialog, a
//     custom 44×44 white-glass close button respecting safe-area
//     insets, counter pill above `env(safe-area-inset-bottom)`.
//   - `fullScreen={false}` (desktop default): `sm:max-w-5xl` dialog
//     with the shadcn default close button, height `h-[80vh]`,
//     counter pinned at `bottom-4`.
//
// Both variants share the same swipe-nav wiring + arrow buttons +
// counter pill semantics, so the two surfaces stay in lock-step.

import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { AuthImg } from "@/components/auth-img";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSwipeNav } from "@/features/property/wizard/use-swipe-nav";

/**
 * Minimal shape the lightbox needs. `PropertyImage` satisfies it
 * structurally; the public share page maps its presigned-URL strings
 * onto it (AuthImg passes absolute URLs through as a plain <img>, so
 * the same component serves both authenticated and public surfaces).
 */
export interface LightboxImage {
  fullUrl: string;
  originalFilename?: string | null;
}

interface PropertyLightboxProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  images: readonly LightboxImage[];
  index: number;
  onPrev: () => void;
  onNext: () => void;
  /**
   * Full-screen black-bg variant — used by the mobile hero. Default
   * false keeps the desktop's previous `sm:max-w-5xl` behaviour so
   * we don't sneak a visual change in under the "refactor" banner.
   */
  fullScreen?: boolean;
}

export function PropertyLightbox({
  open,
  onOpenChange,
  images,
  index,
  onPrev,
  onNext,
  fullScreen = false,
}: PropertyLightboxProps) {
  const total = images.length;
  const safeIndex = total > 0 ? Math.min(index, total - 1) : 0;
  const current = total > 0 ? images[safeIndex] : undefined;
  const swipeHandlers = useSwipeNav({
    onSwipeLeft: onNext,
    onSwipeRight: onPrev,
  });

  if (fullScreen) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="h-screen max-h-screen w-screen max-w-none gap-0 overflow-hidden border-0 bg-black p-0 sm:max-w-none"
        >
          <DialogTitle className="sr-only">Xem ảnh phóng to</DialogTitle>
          <div
            className="relative flex h-full w-full touch-pan-y items-center justify-center select-none"
            {...swipeHandlers}
          >
            {current ? (
              <AuthImg
                src={current.fullUrl}
                alt={current.originalFilename || ""}
                className="max-h-full max-w-full object-contain"
              />
            ) : null}

            {/* Close button — large, visible on the black backdrop, sits
                above the safe-area inset so iPhone notches don't eat it. */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Đóng"
              className="absolute top-[max(env(safe-area-inset-top),16px)] right-3 z-20 flex size-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/25"
            >
              <X aria-hidden="true" className="size-5" />
            </button>

            {total > 1 ? (
              <>
                <button
                  type="button"
                  onClick={onPrev}
                  aria-label="Ảnh trước"
                  className="absolute top-1/2 left-3 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-foreground hover:bg-white"
                >
                  <ChevronLeft aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  aria-label="Ảnh sau"
                  className="absolute top-1/2 right-3 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-foreground hover:bg-white"
                >
                  <ChevronRight aria-hidden="true" />
                </button>
                <div className="absolute bottom-[max(env(safe-area-inset-bottom),24px)] left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-4 py-1.5 font-mono text-[12px] text-white">
                  {safeIndex + 1} / {total}
                </div>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="w-screen max-w-none p-0 sm:max-w-5xl"
      >
        <DialogTitle className="sr-only">Xem ảnh phóng to</DialogTitle>
        <div
          className="relative flex h-[80vh] w-full touch-pan-y items-center justify-center bg-black select-none"
          {...swipeHandlers}
        >
          {current ? (
            <AuthImg
              src={current.fullUrl}
              alt={current.originalFilename ?? ""}
              className="max-h-full max-w-full object-contain"
            />
          ) : null}
          {total > 1 ? (
            <>
              <button
                type="button"
                onClick={onPrev}
                aria-label="Ảnh trước"
                className="absolute top-1/2 left-3 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-foreground hover:bg-white"
              >
                <ChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={onNext}
                aria-label="Ảnh sau"
                className="absolute top-1/2 right-3 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-foreground hover:bg-white"
              >
                <ChevronRight aria-hidden="true" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-4 py-1.5 text-sm text-white">
                {safeIndex + 1} / {total}
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
